// SocketProvider — управляет жизненным циклом реального socket.io-client.
//
// Поток:
//   1. mount → если есть аутентификация → connectSocket({ token })
//   2. logout (isAuthenticated=false) → disconnectSocket()
//   3. unmount → disconnectSocket()
//
// Bearer-токен берётся из localStorage (web) или SecureStorage (Capacitor)
// через единый хелпер `getAccessToken()`. Web также аутентифицируется через
// httpOnly session cookie (withCredentials=true), но Bearer передаём всегда
// — на Capacitor cookies не работают, а на web сервер уже принимает оба способа.
//
// API хука `useSocket()` — возвращает текущий AppSocket | null. Если null —
// пользователь не аутентифицирован или socket ещё не подключился. Consumer'ы
// обычно используют `useSocketEvent(event, handler)` из @/hooks/use-socket-event
// — он сам обрабатывает null.
//
// Жизненный цикл под Capacitor (rules/cross-platform-frontend.md, план §7.6):
// в background socket закрывается, в foreground поднимается заново. Держать
// открытый WebSocket в свёрнутом приложении — прямой расход батареи, а
// восстановление дешёвое: серверный `connectionStateRecovery` (2 мин) отдаёт
// пропущенные события, за его окном добирает обычная invalidation.
// Источник сигнала — `onAppVisibilityChange` (Capacitor appState / web
// visibilitychange), а НЕ второй импорт платформенного адаптера.

import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from "react";
import { isAppVisible, onAppVisibilityChange } from "@/lib/app-visible";
import { getAccessToken } from "@/lib/auth-storage";
import { IS_CAPACITOR } from "@/lib/platform-detect";
import { type AppSocket, connectSocket, disconnectSocket, reviveSocket } from "@/lib/socket-client";
import { useAuth } from "./auth-provider";

/**
 * Пауза перед закрытием socket в background. Переключение на камеру/шторку
 * уведомлений — тоже background: рвать соединение на каждый такой переход
 * дороже, чем подержать его несколько секунд.
 */
const BACKGROUND_DISCONNECT_DELAY_MS = 15_000;

interface SocketContextValue {
	socket: AppSocket | null;
}

const SocketContext = createContext<SocketContextValue | null>(null);

/** Что сделать с соединением при переходе «подключены для A» → «сейчас B». */
export type SocketTransition =
	| "noop" // тот же пользователь — соединение переиспользуется
	| "connect" // не были подключены, появился пользователь
	| "reconnect" // сменился пользователь: рвём прежнее и поднимаем заново
	| "disconnect"; // пользователя нет — закрываем

/**
 * Решение принимается по `userId`, а НЕ по булеву «залогинен».
 *
 * `connectSocket` идемпотентен по факту живого сокета (`connected || active`), а
 * не по токену. Поэтому на входе другим аккаунтом БЕЗ промежуточного разлогина
 * (протухшая сессия → вход другим, вторая вкладка, оборванный logout, ротация
 * access-токена) `isAuthenticated` не падает в `false`, эффект получает ПРЕЖНИЙ
 * сокет, и новый пользователь продолжает слушать room `user:<предыдущий id>` —
 * чужие уведомления и события генерации вплоть до перезагрузки страницы.
 * Тот же инвариант, что в `powersync-provider.tsx` (сравнение по `user.id`).
 *
 * Чистая функция вынесена наружу, чтобы инвариант проверялся тестом без jsdom.
 */
export function planSocketTransition(
	connectedFor: string | null,
	userId: string | null,
): SocketTransition {
	if (!userId) return connectedFor === null ? "noop" : "disconnect";
	if (connectedFor === userId) return "noop";
	return connectedFor === null ? "connect" : "reconnect";
}

export function SocketProvider({ children }: { children: ReactNode }) {
	const { isAuthenticated, isLoading, user } = useAuth();
	const userId = isAuthenticated ? (user?.id ?? null) : null;
	/** Для КОГО поднято соединение (см. `planSocketTransition`). */
	const connectedForRef = useRef<string | null>(null);
	const [socket, setSocket] = useState<AppSocket | null>(null);

	useEffect(() => {
		// Ждём первичную загрузку сессии — иначе на cold start с просроченной
		// сессией мы попробуем подключиться без токена и сервер бросит UNAUTHORIZED.
		if (isLoading) return;

		const transition = planSocketTransition(connectedForRef.current, userId);
		if (transition === "noop") return;

		if (transition === "disconnect") {
			connectedForRef.current = null;
			disconnectSocket();
			setSocket(null);
			return;
		}
		// `reconnect`: рвём прежний сокет ДО подключения, иначе идемпотентный
		// `connectSocket` вернёт соединение предыдущего пользователя.
		if (transition === "reconnect") disconnectSocket();
		connectedForRef.current = userId;
		setSocket(connectSocket({ token: getAccessToken() }));
	}, [isLoading, userId]);

	// Capacitor: background → закрыть, foreground → поднять заново.
	// Web-вкладку не трогаем: скрытая вкладка не стоит батареи, а разрыв там
	// ломал бы realtime при обычном переключении окон.
	useEffect(() => {
		if (!IS_CAPACITOR || isLoading || !isAuthenticated) return;

		let timer: ReturnType<typeof setTimeout> | undefined;
		const unsubscribe = onAppVisibilityChange(() => {
			clearTimeout(timer);
			timer = undefined;
			if (isAppVisible()) {
				// `connectSocket` идемпотентен: если разрыва не случилось
				// (вернулись раньше задержки), вернётся тот же экземпляр и
				// подписчики не переподключатся.
				setSocket(connectSocket({ token: getAccessToken() }));
				return;
			}
			timer = setTimeout(() => {
				disconnectSocket();
				setSocket(null);
			}, BACKGROUND_DISCONNECT_DELAY_MS);
		});

		return () => {
			clearTimeout(timer);
			unsubscribe();
		};
	}, [isAuthenticated, isLoading]);

	// Web: после долгого офлайна / деплоя socket.io мог сдаться. Capacitor
	// поднимает сокет заново из visibility-эффекта выше; на вебе — revival
	// по `online` и возврату вкладки.
	useEffect(() => {
		if (IS_CAPACITOR || isLoading || !isAuthenticated) return;
		const revive = () => {
			setSocket(reviveSocket({ token: getAccessToken() }));
		};
		const onVisible = () => {
			if (document.visibilityState === "visible") revive();
		};
		window.addEventListener("online", revive);
		document.addEventListener("visibilitychange", onVisible);
		return () => {
			window.removeEventListener("online", revive);
			document.removeEventListener("visibilitychange", onVisible);
		};
	}, [isAuthenticated, isLoading]);

	// Финальный teardown при размонтировании провайдера (закрытие приложения).
	useEffect(() => {
		return () => {
			connectedForRef.current = null;
			disconnectSocket();
		};
	}, []);

	return <SocketContext.Provider value={{ socket }}>{children}</SocketContext.Provider>;
}

/**
 * Возвращает текущий Socket.IO client (или null, если не подключён).
 *
 * Для подписки на event'ы предпочтительно использовать `useSocketEvent` —
 * он сам обрабатывает null + cleanup.
 */
export function useSocket(): AppSocket | null {
	const ctx = useContext(SocketContext);
	if (!ctx) {
		throw new Error("useSocket must be used within <SocketProvider>");
	}
	return ctx.socket;
}
